import React, { useContext, useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import BdsTooltip from '../../../../../shared/components/v3/BdsTooltip/BdsTooltip';
import BdsButton from '../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import SelectionCard from '../../../../../shared/components/v3/Cards/SelectionCard/SelectionCard';
import {
  MANAGE_COLLABORATORS_CARDS,
  MANAGE_COLLABORATORS_WITH_DEPENDENTS_CARDS
} from '../../../../../shared/constants/collaborators';
import { ARROW_RIGHT_ICON } from '../../../../../shared/constants/images';
import {
  CLICK_ACTION,
  COLLABORATOR_CHOICE_DICT,
  MANAGE_COLLABORATORS_SELECTION
} from '../../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { IManageCollaboratorsOptions } from '../../../../../shared/interfaces/manageCollaborators';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_MANAGE_COLLABORATORS } from '../../../../../shared/translations/manageCollaborators';
import { isCurrentDayFromDayOfMonth } from '../../../../../shared/utils/datetime/datetime';
import './ManageCollaboratorsOptions.scss';

const ManageCollaboratorsOptions = ({
  setOption,
  optionSelected,
  handleNextClick,
  hasDependent,
  loading = false
}: IManageCollaboratorsOptions) => {
  const [disableButton, setDisableButton] = useState<boolean>(true);

  const { isMexico } = useContext(CompanyContext);

  const { t } = useTranslation();

  const disableDependents = useMemo(() => isMexico && isCurrentDayFromDayOfMonth(25), [isMexico]);

  const onNextClick = () => {
    TagManagerService.pushEvent(CLICK_ACTION, MANAGE_COLLABORATORS_SELECTION, COLLABORATOR_CHOICE_DICT[optionSelected]);
    handleNextClick(optionSelected);
  };

  const handleOnChange = (value: string): void => {
    if (value === optionSelected) return;
    setOption(value);
    setDisableButton(false);
  };

  useEffect(() => {
    if (optionSelected) setDisableButton(false);
  }, [optionSelected]);

  return (
    <>
      <div className="manager-collaborators-options">
        {MANAGE_COLLABORATORS_CARDS.map((option) => (
          <SelectionCard
            key={`collaborators-card-${option.value}`}
            type="collaborators-card"
            title={t(option.title)}
            description={t(option.description)}
            paddingSize="padding-l"
            radiusSize="radius-l"
            pictogram={option.pictogram}
            name={option.value}
            value={option.value}
            selected={option.value === optionSelected}
            onChange={handleOnChange}
          />
        ))}
        {hasDependent && (
          <BdsTooltip
            message={t(TRANSLATION_MANAGE_COLLABORATORS.manageCollaboratorsOptions.addDependentsCard.tooltip)}
            renderTooltip={disableDependents}
            position="top-center"
          >
            <SelectionCard
              key={`collaborators-card-${MANAGE_COLLABORATORS_WITH_DEPENDENTS_CARDS.value}`}
              type="collaborators-card"
              title={t(MANAGE_COLLABORATORS_WITH_DEPENDENTS_CARDS.title)}
              description={t(MANAGE_COLLABORATORS_WITH_DEPENDENTS_CARDS.description)}
              paddingSize="padding-l"
              radiusSize="radius-l"
              pictogram={MANAGE_COLLABORATORS_WITH_DEPENDENTS_CARDS.pictogram}
              name={MANAGE_COLLABORATORS_WITH_DEPENDENTS_CARDS.value}
              value={MANAGE_COLLABORATORS_WITH_DEPENDENTS_CARDS.value}
              selected={MANAGE_COLLABORATORS_WITH_DEPENDENTS_CARDS.value === optionSelected}
              onChange={handleOnChange}
              disabled={disableDependents}
            />
          </BdsTooltip>
        )}
        {loading && <Skeleton variant="rectangular" className="card-skeleton" />}
      </div>
      <div className="manage-collaborators-button">
        <BdsButton
          variant="primary"
          size="l"
          action={onNextClick}
          disabled={disableButton}
          iconRight={ARROW_RIGHT_ICON}
        >
          {t(TRANSLATION_MANAGE_COLLABORATORS.buttonStart)}
        </BdsButton>
      </div>
    </>
  );
};

export default ManageCollaboratorsOptions;
